import { Transaction } from '@features/transactions/types/transaction'
import { Posting } from '@modules-deprecated/bankReconciliation/types'

import { Metable } from '../../types/metable'
import { TaxReturn } from '../../types/taxReturn'
import { getCurrentLocale, getRequest, postRequest, putRequest } from '../../utils'
import { VatDeclarationAuditTrailData } from './elements/VatDeclaration/elements/VatDeclarationReport/elements/AuditTrail/types/AuditTrail'
import { VatDeclarationPayment } from './types/vatDeclarationPayment'
import { VatDeclarationSubmission } from './types/vatDeclarationSubmission'

// We're renaming things to "vatDeclarations" on the frontend for clarity,
// but keeping API paths and function names using "salesTax" until the backend is updated.
// Full refactor will follow later.

export interface VatDeclarationsResponse {
  salesTaxReturns: TaxReturn[]
}

export const fetchVatDeclarations = (organizationId: string): Promise<VatDeclarationsResponse> => {
  const locale = getCurrentLocale()
  return getRequest(
    `/v2/salesTaxReturns?organizationId=${organizationId}&sortProperty=startDate&sortDirection=DESC&pageSize=100&offset=0`,
    { 'accept-language': locale },
  )
}

interface UpdateVatDeclarationSettlementPayload {
  id: string
  isSettled: boolean
  customVatPayoutAmount?: number
}

export const updateVatDeclarationSettlement = ({
  id,
  isSettled,
  customVatPayoutAmount,
}: UpdateVatDeclarationSettlementPayload) => {
  const locale = getCurrentLocale()
  return putRequest(
    `/v2/salesTaxReturns/${id}`,
    {
      salesTaxReturn: {
        customVatPayoutAmount,
        id,
        isSettled,
      },
    },
    { 'accept-language': locale },
  )
}

export interface RegisterVatDeclarationPayload {
  accountId: string
  entryDate: string
  returnId: string
}

export const registerVatDeclarationPayment = (payload: RegisterVatDeclarationPayload) => {
  const locale = getCurrentLocale()
  return postRequest(
    `/v2/salesTaxPayments`,
    {
      salesTaxPayment: payload,
    },
    { 'accept-language': locale },
  )
}

export interface VatDeclarationPaymentsResponse extends Metable {
  salesTaxPayments: VatDeclarationPayment[]
}

export const fetchVatDeclarationPayments = (vatDeclarationId: string): Promise<VatDeclarationPaymentsResponse> => {
  return getRequest(`/v2/salesTaxPayments?returnId=${vatDeclarationId}&isVoided=false`)
}

export const voidVatDeclarationPayment = (vatDeclarationId: string) => {
  const locale = getCurrentLocale()
  return putRequest(
    `/v2/salesTaxPayments/${vatDeclarationId}`,
    {
      salesTaxPayment: {
        id: vatDeclarationId,
        isVoided: true,
      },
    },
    { 'accept-language': locale },
  )
}

interface FetchVatDeclarationAuditTrailPayload {
  showVoided: boolean
  vatDeclarationId: string
}

interface VatDeclarationAuditTrailResponse {
  data: VatDeclarationAuditTrailData
}

export const fetchVatDeclarationAuditTrail = ({
  showVoided,
  vatDeclarationId,
}: FetchVatDeclarationAuditTrailPayload): Promise<VatDeclarationAuditTrailResponse> => {
  return getRequest(`/v2/salesTaxReturns/${vatDeclarationId}/accountAuditTrail?hideVoided=${!showVoided}`)
}

export interface ChangeVatDeclarationPayload {
  targetSalesTaxPeriod: string
}

export const changeVatDeclarationPeriod = (vatDeclarationId: string, payload: ChangeVatDeclarationPayload) => {
  const locale = getCurrentLocale()
  return postRequest(
    `/v2/salesTaxReturns/${vatDeclarationId}/periodChange`,
    {
      periodChange: payload,
    },
    { 'accept-language': locale },
  )
}

export interface SubmitVatDeclarationPayload {
  contactInitials: string
  contactSurname: string
  contactSurnamePrefix?: string
  taxIdentificationNumber: string
}

export interface SubmitVatDeclarationOptions {
  payload: SubmitVatDeclarationPayload
  salesTaxReturnId: string
}

export interface SubmitVatDeclarationResponse {
  transactions: Transaction[]
  postings: Posting[]
  salesTaxReturns: TaxReturn[]
  salesTaxReturnSubmissions: VatDeclarationSubmission[]
}

export const submitVatDeclaration = ({
  payload,
  salesTaxReturnId,
}: SubmitVatDeclarationOptions): Promise<SubmitVatDeclarationResponse> =>
  postRequest(`/v2/salesTaxReturns/${salesTaxReturnId}/submit`, {
    record: payload,
  })

interface GetVatDeclarationLogsParams {
  organizationId: string
  salesTaxReturnIds: string
}

export interface GetVatDeclarationSubmissionsResponse {
  salesTaxReturnSubmissions: VatDeclarationSubmission[]
}

export const getVatDeclarationSubmissions = ({
  organizationId,
  salesTaxReturnIds,
}: GetVatDeclarationLogsParams): Promise<GetVatDeclarationSubmissionsResponse> => {
  return getRequest(
    `/v2/salesTaxReturnSubmissions?salesTaxReturnIds=${salesTaxReturnIds}&organizationId=${organizationId}`,
  )
}

interface SubmitToSkatProps {
  organizationId: string
  declarationId: string
  registrationNumber: string
}

export const submitToSkat = ({ organizationId, declarationId, registrationNumber }: SubmitToSkatProps) => {
  return postRequest(`/integrations/skat/organizations/${organizationId}/vat-returns`, {
    salesTaxReturnId: declarationId,
    registrationNumber,
  })
}
